import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import BenefitPeriodCard from './benefit-period-card.gts';
import {
  buildBenefitPeriodCardModel,
  shouldShowNextWindow,
} from '../../utils/tuition-assistance/limit-summary';
import { fetchLimitSummaryCached } from '../../utils/tuition-assistance/limit-summary-cache';
import type { BenefitPeriodCardModel } from '../../utils/tuition-assistance/limit-summary';
import type { Store as StoreService } from '@warp-drive/core';
import type { IntlService } from 'ember-intl';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';

interface BenefitSummaryCardsSignature {
  Args: {
    employeeId: string;
    externalSpendLabel: string;
    /**
     * 'history' (default) renders the current-year card plus a next-year card
     * once the next fiscal window starts within 6 months; 'dashboard' renders
     * a single hero card with the over-limit banner.
     */
    variant?: 'history' | 'dashboard';
    /** Enables the partner popover on the external legend entry. */
    employee?: EmployeeModel;
  };
  Element: HTMLDivElement;
}

/**
 * Benefit remaining cards sourced from /api/v2/limits/summary — the same
 * server-computed tree the limit-tree chart renders (tree.TA / tree.SLR +
 * company rollup), replacing the FE-derived math over /limits/status.
 */
export default class BenefitSummaryCards extends Component<BenefitSummaryCardsSignature> {
  @service declare store: StoreService;
  @service declare intl: IntlService;

  get variant() {
    return this.args.variant ?? 'history';
  }

  // Bumped when a background revalidation lands so the models re-derive
  // from the refreshed cache without a loading flash.
  @tracked refreshEpoch = 0;

  #revalidated = () => {
    this.refreshEpoch++;
  };

  summaryData = trackedFunction(this, async () => {
    void this.refreshEpoch;
    const employeeId = this.args.employeeId;
    if (!employeeId) return null;

    const current = buildBenefitPeriodCardModel(
      await fetchLimitSummaryCached(this.store, { employeeId }, this.#revalidated)
    );

    let next: BenefitPeriodCardModel | null = null;
    const nextDate = current.nextWindowFetchDate;
    if (this.variant === 'history' && nextDate && shouldShowNextWindow(nextDate)) {
      next = buildBenefitPeriodCardModel(
        await fetchLimitSummaryCached(this.store, { employeeId, date: nextDate }, this.#revalidated)
      );
    }

    const result = { current, next };
    this.#lastResult = { employeeId, ...result };
    return result;
  });

  // Retains the last models across re-runs so revalidation updates in place;
  // keyed by employee so a changed @employeeId never shows the previous
  // employee's numbers while the new fetch is loading. Written by the
  // trackedFunction, keeping this getter side-effect free.
  #lastResult: {
    employeeId: string;
    current: BenefitPeriodCardModel;
    next: BenefitPeriodCardModel | null;
  } | null = null;

  get result() {
    const value = this.summaryData.value;
    if (value) return value;
    return this.#lastResult?.employeeId === this.args.employeeId ? this.#lastResult : null;
  }

  get current(): BenefitPeriodCardModel {
    return this.result?.current ?? buildBenefitPeriodCardModel(null);
  }

  get next() {
    return this.result?.next ?? null;
  }

  get isLoading() {
    return this.summaryData.isLoading && !this.result;
  }

  get gridClass() {
    return this.next ? 'grid gap-4 md:grid-cols-2' : 'grid gap-4 md:grid-cols-1';
  }

  get showDashboardCard() {
    return !this.isLoading && this.current.mode !== 'empty';
  }

  <template>
    {{#if (eq this.variant "dashboard")}}
      {{#if this.showDashboardCard}}
        <BenefitPeriodCard
          @model={{this.current}}
          @size="lg"
          @showOverLimitBanner={{true}}
          @employee={{@employee}}
          @externalSpendLabel={{@externalSpendLabel}}
          data-test-benefit-summary-dashboard-card
          ...attributes
        />
      {{/if}}
    {{else}}
      <div class="tio-col-loose" data-test-benefit-summary-cards ...attributes>
        <div class={{this.gridClass}}>
          {{#if this.isLoading}}
            <div
              class="border tio-border-default rounded-xl p-4 tio-fg-muted"
              data-test-benefit-summary-loading
            >
              {{t "tas.dashboard.annual_benefits.loading"}}
            </div>
          {{else}}
            <div class="border tio-border-default rounded-xl p-4">
              <BenefitPeriodCard
                @model={{this.current}}
                @label={{t "tas.benefit_history.current_year"}}
                @employee={{@employee}}
                @externalSpendLabel={{@externalSpendLabel}}
                data-test-benefit-summary-current
              />
            </div>
            {{#if this.next}}
              <div class="border tio-border-default rounded-xl p-4">
                <BenefitPeriodCard
                  @model={{this.next}}
                  @label={{t "tas.benefit_history.next_year"}}
                  @employee={{@employee}}
                  @externalSpendLabel={{@externalSpendLabel}}
                  data-test-benefit-summary-next
                />
              </div>
            {{/if}}
          {{/if}}
        </div>
      </div>
    {{/if}}
  </template>
}
